import React from 'react';
import { AlertCircle, Clock, CreditCard, CheckCircle, Zap } from 'lucide-react';
import { usePlanStatus, PlanBucketStatus, trialDaysLeft, trialExpiredMessage } from '@/hooks/usePlanStatus';
import { useNavigate } from 'react-router-dom';
import { usePayment } from '@/features/payments';

interface PlanStatusAlertProps {
  showOnlyBlocked?: boolean;
  className?: string;
}

function BucketRow({
  title,
  bucket,
  planLabel,
  highlight,
}: {
  title: string;
  bucket: PlanBucketStatus;
  planLabel: string;
  highlight?: boolean;
}) {
  const remaining = bucket.remaining ?? 0;
  const limit = bucket.limit ?? 0;
  const usage = bucket.usage ?? 0;
  const pct = limit > 0 ? Math.min(100, (usage / limit) * 100) : 0;

  return (
    <div className={`rounded-md border px-3 py-2 ${highlight ? 'border-current/40 bg-white/40' : 'border-transparent bg-white/20'}`}>
      <div className="flex items-center justify-between gap-2 text-xs font-semibold">
        <span>{title}: {planLabel}</span>
        <span>
          {remaining} / {limit} msgs
          {bucket.period_expired ? ' · vencida' : ''}
          {bucket.usable === false && !bucket.period_expired ? ' · agotada' : ''}
        </span>
      </div>
      {limit > 0 && (
        <div className="mt-1.5 w-full bg-white/50 rounded-full h-1.5">
          <div className="h-1.5 rounded-full bg-current opacity-70" style={{ width: `${pct}%` }} />
        </div>
      )}
      {typeof bucket.balance_amount === 'number' && bucket.billing_type === 'postpago' && (
        <p className="mt-1 text-[11px] opacity-80">
          Saldo ~ ${Math.round(bucket.balance_amount).toLocaleString('es-CO')} COP
        </p>
      )}
    </div>
  );
}

export const PlanStatusAlert: React.FC<PlanStatusAlertProps> = ({
  showOnlyBlocked = false,
  className = ""
}) => {
  const { planStatus, loading, isBlocked, getPlanDisplayName } = usePlanStatus();
  const navigate = useNavigate();
  const { enabled: paymentsEnabled } = usePayment();

  if (loading || !planStatus) return null;

  if (showOnlyBlocked && !isBlocked) return null;

  const hasSub = !!planStatus.subscription;
  const hasPrepaid = !!planStatus.prepaid;
  const dual = hasSub && hasPrepaid;

  let alertType: 'blocked' | 'warning' | 'info' | 'success' = 'info';
  let message = '';
  let icon = <CreditCard className="h-5 w-5" />;

  if (planStatus.is_blocked) {
    alertType = 'blocked';
    icon = <AlertCircle className="h-5 w-5" />;

    if (planStatus.trial_expired) {
      message = trialExpiredMessage(planStatus);
    } else if (planStatus.subscription_expired && !hasPrepaid) {
      message = 'Tu suscripción ha expirado y no tienes saldo prepago. Renueva o recarga para continuar.';
    } else {
      const limitText = planStatus.limit ? ` de ${planStatus.limit} mensajes` : '';
      message = `Has alcanzado el límite${limitText} de mensajes disponibles.`;
    }
  } else if (planStatus.plan === 'unlimited') {
    alertType = 'success';
    icon = <CheckCircle className="h-5 w-5" />;
    message = `Plan ${getPlanDisplayName(planStatus.plan)} • ${planStatus.usage} mensajes entregados este mes`;
  } else if (dual) {
    alertType = (planStatus.remaining !== null && planStatus.limit && planStatus.remaining <= planStatus.limit * 0.2)
      ? 'warning'
      : 'info';
    icon = alertType === 'warning' ? <Clock className="h-5 w-5" /> : <CreditCard className="h-5 w-5" />;
    const active = planStatus.active_bucket === 'prepaid' ? 'prepago' : 'suscripción';
    message = `Usando ${active} primero. Suscripción tiene prioridad mientras tenga cupo del mes.`;
  } else if (planStatus.remaining !== null && planStatus.limit && planStatus.remaining <= (planStatus.limit * 0.2)) {
    alertType = 'warning';
    icon = <Clock className="h-5 w-5" />;
    message = `Te quedan ${planStatus.remaining} mensajes de ${planStatus.limit}. Considera actualizar tu plan.`;
  } else if (!showOnlyBlocked) {
    alertType = 'info';
    message = `Plan ${getPlanDisplayName(planStatus.plan)} • ${planStatus.remaining} de ${planStatus.limit} mensajes disponibles`;
  } else {
    return null;
  }

  const daysLeft = planStatus.is_blocked ? null : trialDaysLeft(planStatus);
  if (daysLeft !== null) {
    message += ` • Te ${daysLeft === 1 ? 'queda 1 día' : `quedan ${daysLeft} días`} de prueba`;
  }

  const alertStyles = {
    blocked: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    success: 'bg-green-50 border-green-200 text-green-800'
  };

  const iconStyles = {
    blocked: 'text-red-600',
    warning: 'text-yellow-600',
    info: 'text-blue-600',
    success: 'text-green-600'
  };

  return (
    <div className={`rounded-lg border p-4 ${alertStyles[alertType]} ${className}`}>
      <div className="flex items-start gap-3">
        <div className={`${iconStyles[alertType]} mt-0.5`}>
          {icon}
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <p className="text-sm font-medium">{message}</p>

          {dual ? (
            <div className="space-y-2">
              {planStatus.subscription && (
                <BucketRow
                  title="Suscripción"
                  bucket={planStatus.subscription}
                  planLabel={getPlanDisplayName(planStatus.subscription.plan_type || planStatus.plan)}
                  highlight={planStatus.active_bucket === 'subscription'}
                />
              )}
              {planStatus.prepaid && (
                <BucketRow
                  title="Prepago"
                  bucket={planStatus.prepaid}
                  planLabel="Postpago"
                  highlight={planStatus.active_bucket === 'prepaid'}
                />
              )}
            </div>
          ) : (
            (planStatus.limit ?? 0) > 0 && (
              <div className="mt-1">
                <div className="w-full bg-white bg-opacity-50 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${alertType === 'blocked' ? 'bg-red-500' :
                      alertType === 'warning' ? 'bg-yellow-500' :
                        alertType === 'success' ? 'bg-green-500' :
                          'bg-blue-500'
                      }`}
                    style={{ width: `${Math.min(100, ((planStatus.usage || 0) / (planStatus.limit || 1)) * 100)}%` }}
                  />
                </div>
              </div>
            )
          )}
        </div>

        {paymentsEnabled && (
          <button
            onClick={() => navigate('/payment/checkout')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-sm shrink-0 whitespace-nowrap text-white bg-[#1CB3B3] hover:bg-[#169a9a] active:bg-[#138080]`}
          >
            <Zap className="h-4 w-4" />
            Recargar Saldo
          </button>
        )}
      </div>
    </div>
  );
};
